<template>
  <PeopleDrawer
    v-if="selectedPersonId"
    v-model:drawer-visibility="peopleDrawerVisible"
    :person-id="selectedPersonId"
    @close="handlePeopleDrawerClose" />

  <div v-if="ownersCount > 0" class="flex items-center gap-3">
    <p class="mt-1 whitespace-nowrap font-semibold text-contrast-muted">
      {{ i18n.t('views.applicationDetails.ownerTag.label') }}
    </p>

    <div class="relative ml-2 flex">
      <button
        v-for="(entry, index) in ownerList"
        :key="entry.id ?? index"
        type="button"
        class="owner-icon-wrapper"
        :aria-label="`${i18n.t('views.applicationDetails.ownerTag.label')}: ${entry.name}`"
        @click="openPeopleDrawer(entry.id)"
        @keydown.enter.prevent="openPeopleDrawer(entry.id)"
        @keydown.space.prevent="openPeopleDrawer(entry.id)">
        <SmTooltip>
          <template #default>
            <SmAvatar
              class="owner-avatar"
              :name="entry.name"
              size="xsmall"
              form="circle"
              :background-color="entry.color" />
          </template>
          <template #content>
            <span>{{ entry.name }}</span>
          </template>
        </SmTooltip>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch, defineProps, PropType } from 'vue'
  import SmAvatar from '@/components/sm/SmAvatar.vue'
  import { PersonStore } from '@/stores/person/person.store'
  import { useI18n } from 'vue-i18n'
  import PeopleDrawer from '@/components/people/PeopleDrawer.vue'

  const i18n = useI18n()
  const props = defineProps({
    ownerAccountIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  })

  const UNKNOWN_OWNER_NAME = 'Unknown'
  const ownersCount = computed(() => props.ownerAccountIds?.length ?? 0)
  const ownerList = ref<{ id: string; name: string; color: string }[]>([])
  const selectedPersonId = ref<string | null>(null)
  const peopleDrawerVisible = ref(false)

  const OWNER_COLORS = [
    'var(--sm-yellow)',
    'var(--sm-orange)',
    'var(--sm-lime)',
    'var(--sm-green)',
    'var(--sm-blue)',
    'var(--sm-cyan)',
    'var(--sm-purple)',
    'var(--sm-magenta)',
    'var(--sm-petrol-light)',
    'var(--sm-primary)',
  ] as const

  function getColorForOwner(fallbackIndex: number) {
    return OWNER_COLORS[fallbackIndex % OWNER_COLORS.length]
  }

  async function getPersonName(personId: string) {
    const person = await PersonStore.getPersonById(personId)
    return person?.name ?? UNKNOWN_OWNER_NAME
  }

  async function createOwnerList(ids: string[]) {
    ownerList.value = await Promise.all(
      ids.map(async (id, index) => ({
        id,
        name: await getPersonName(id),
        color: getColorForOwner(index),
      }))
    )
  }
  watch(
    () => props.ownerAccountIds,
    (ids) => {
      createOwnerList(ids || [])
    },
    { immediate: true }
  )

  function openPeopleDrawer(ownerId?: string | null) {
    if (!ownerId) return
    selectedPersonId.value = ownerId
    peopleDrawerVisible.value = true
  }

  function handlePeopleDrawerClose() {
    selectedPersonId.value = null
  }
</script>

<style scoped lang="scss">
  $avatar-size: 22px;

  .owner-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $avatar-size;
    min-width: $avatar-size;
    height: $avatar-size;
    background-color: var(--sm-background);
    box-shadow: var(--shadow);
    border-radius: 50%;
    position: relative;
    padding: 2px;
    border: none;
    cursor: pointer;
    transition:
      transform 0.2s ease-in-out,
      box-shadow 0.2s ease-in-out;
  }

  .owner-icon-wrapper:hover {
    box-shadow: shadow;
    z-index: 1000;
    transform: scale(1.05) translateZ(0);
  }

  .owner-icon-wrapper:focus-visible {
    outline: 2px solid var(--sm-primary);
    outline-offset: 2px;
  }

  @for $i from 1 through 10 {
    .owner-icon-wrapper:nth-child(#{$i}) {
      z-index: $i;
      transform: translateX(calc(-#{$i} * $avatar-size / 2));
    }
  }

  @for $i from 1 through 10 {
    .owner-icon-wrapper:hover ~ .owner-icon-wrapper:nth-child(#{$i}) {
      transform: translateX(calc(-#{$i} * $avatar-size / 2 + $avatar-size / 2));
    }
  }

  .owner-avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: none;
    overflow: hidden;
  }

  .owner-avatar :deep(.aspect-square),
  .owner-avatar :deep(svg) {
    width: 100%;
    height: 100%;
  }
</style>
